import React, { useEffect, useState } from "react";
import ModalSendCode from "../../components/ModalSendCode";
import { socket } from "../../services/socket";
import MeetingOneToThree from "./MeetingOneToThree";
import MeetingWhatsApp from "./MeetingWhatsApp";
import MeetingGrid from "./MeetingGrid";
import { useMeeting } from "../../hooks/useMeeting";
import { useMediasoupClient } from "../../hooks/useMediasoupClient";
import AlertNewParticipant from "../AlertNewParticipant";
import AlertPriseDeParole from "../AlertPriseDeParole";
import {
  consumeRemoteStream,
  groupConsumersByUser,
  receiveTransportListenEvents,
} from "../../services/mediasoup-client";

export default function MeetingContainer({ mediasTools }) {
  const {
    stream,
    nombreParticipants,
    msgNewPeer,
    setMsgNewPeer,
    nouveauParticipant,
    setNouveauParticipant,
    showCodeOrLink,
    setShowCodeOrLink,
    accessCode,
    raisedHands,
    degradedUserIds,
  } = mediasTools;
  const { localStream, setParticipants, participants } = useMeeting();
  const { recvTransport } = useMediasoupClient();

  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 576);
      setIsDesktop(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!recvTransport) return;

    receiveTransportListenEvents({ receiveTransport: recvTransport });

    const handleNewParticipant = async ({ consumers, msg, participant }) => {
      setMsgNewPeer(msg);
      setNouveauParticipant({
        image: participant.userImage,
        nom: participant.displayName.split(" ")[1],
        prenom: participant.displayName.split(" ")[0],
        userId: participant.userId,
        colors: participant.colors,
        exit: false,
      });
      for (const consumer of groupConsumersByUser(consumers)) {
        await consumeRemoteStream(consumer, recvTransport, setParticipants);
      }
    };

    const handleChangeVideo = (peer) => {
      setParticipants((prev) =>
        prev.map((p) =>
          p.userId === peer.userId
            ? {
                ...p,
                videoOn: peer.videoOn,
                producerPaused: peer.producerPaused,
              }
            : p
        )
      );
    };

    const handleChangeAudio = (peer) => {
      const { userId, audioOn, producerPaused } = peer;
      setParticipants((prev) =>
        prev.map((p) =>
          p.userId === userId ? { ...p, audioOn, producerPaused } : p
        )
      );
    };

    const handleActiveSpeaker = ({ peerId }) => {
      setActiveSpeakerId(peerId);
    };

    const handleConsumerClosed = ({ consumerId, userId }) => {
      if (!userId) return;
      setParticipants((prev) =>
        prev.map((p) => {
          if (p.userId !== userId) return p;
          if (!p.stream || p.isLocal) return p;
          const tracks = p.stream.getTracks();
          tracks.forEach((track) => {
            if (track.readyState === "ended") {
              p.stream.removeTrack(track);
            }
          });
          return { ...p };
        }).filter((p) => {
          if (p.isLocal) return true;
          if (p.userId !== userId) return true;
          if (!p.stream) return false;
          return p.stream.getTracks().length > 0;
        })
      );
    };

    socket.on("new_participant", handleNewParticipant);
    socket.on("change_your_ui_for_toggle_producer_video", handleChangeVideo);
    socket.on("change_your_ui_for_toggle_producer_audio", handleChangeAudio);
    socket.on("active_speaker", handleActiveSpeaker);
    socket.on("consumer_closed", handleConsumerClosed);

    return () => {
      socket.off("new_participant", handleNewParticipant);
      socket.off("change_your_ui_for_toggle_producer_video", handleChangeVideo);
      socket.off("change_your_ui_for_toggle_producer_audio", handleChangeAudio);
      socket.off("active_speaker", handleActiveSpeaker);
      socket.off("consumer_closed", handleConsumerClosed);
    };
  }, [socket, recvTransport]);

  return (
    <>
      {nouveauParticipant ? (
        <AlertNewParticipant
          peer={nouveauParticipant}
          message={msgNewPeer}
          setNouveauParticipant={setNouveauParticipant}
          exit={nouveauParticipant.exit}
        />
      ) : (
        <></>
      )}

      <div style={{ position: "relative" }}>
        {isDesktop && nombreParticipants <= 3 ? (
          <MeetingOneToThree
            users={participants}
            mediasTools={{
              localStream: localStream || stream,
              degradedUserIds,
            }}
            activeSpeakerId={activeSpeakerId}
          />
        ) : !isDesktop && nombreParticipants <= 2 ? (
          <MeetingWhatsApp
            users={participants}
            mediasTools={{
              localStream: localStream || stream,
              degradedUserIds,
            }}
            activeSpeakerId={activeSpeakerId}
          />
        ) : (
          <MeetingGrid
            users={participants}
            mediasTools={{
              localStream: localStream || stream,
              degradedUserIds,
            }}
            activeSpeakerId={activeSpeakerId}
            isMobile={isMobile}
          />
        )}

        {raisedHands &&
          raisedHands.map((peer, idx) => (
            <span key={`raised_hands_updated_${peer.userId}`}>
              <AlertPriseDeParole peer={peer} idx={idx} />
            </span>
          ))}
      </div>

      {showCodeOrLink ? (
        <ModalSendCode
          setShowCodeOrLink={setShowCodeOrLink}
          showCodeOrLink={showCodeOrLink}
          accessCode={accessCode}
        />
      ) : (
        <></>
      )}
    </>
  );
}
